import { z, ZodType } from 'zod';

import { validateDateOfBirth, validateFullName, validatePhone } from '@/lib/helpers/formValidation';
import { CustomerData } from '@/types/customer.types';

// Create a dynamic schema based on required fields
export const createPatientFormSchema = (requiredFields: string[]): ZodType<CustomerData> => {
  const baseSchema = z.object({
    name: requiredFields.includes('name')
      ? z
          .string({
            required_error: 'Your full name is required',
          })
          .trim()
          .min(4, {
            message: 'First and last name are required',
          })
          .refine((value: string) => validateFullName(value), {
            message: 'First and last name are required',
          })
      : z.string().optional(),

    phone: requiredFields.includes('phone')
      ? z
          .string({
            required_error: 'Phone number is required',
          })
          .trim()
          .min(10, { message: 'Phone number must be at least 10 digits' })
          .max(15, { message: 'Phone number must not exceed 15 digits' })
          .refine((value: string) => validatePhone(value), {
            message: 'Phone number must contain only numbers',
          })
      : z.string().optional(),

    email: requiredFields.includes('email')
      ? z
          .string({
            required_error: 'Email address is required',
          })
          .trim()
          .email({
            message: 'Please enter a valid email address',
          })
      : z.string().optional(),

    date_of_birth: requiredFields.includes('date_of_birth')
      ? z
          .string({
            required_error: 'Date of birth is required',
          })
          .trim()
          .refine((value: string) => validateDateOfBirth(value), {
            message: 'Please enter a valid date (YYYY-MM-DD)',
          })
          .refine(
            (value: string) => {
              const birthDate = new Date(value);
              const today = new Date();
              const age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();

              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                return age - 1 >= 0;
              }
              return age >= 0 && age <= 150;
            },
            {
              message: 'Please enter a valid birth date',
            },
          )
          .refine(
            (value: string) => {
              const birthDate = new Date(value);
              const today = new Date();
              return birthDate <= today;
            },
            {
              message: 'Birth date cannot be in the future',
            },
          )
      : z.string().optional(),
  });

  return baseSchema as ZodType<CustomerData>;
};

// Keep the original schema for backward compatibility
export const PatientFormSchema: ZodType<CustomerData> = createPatientFormSchema([
  'name',
  'phone',
  'email',
]);
